import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import ChatService from '../../services/chatService';

const TicketDetail = ({ ticket, isOpen, onClose, onUpdate }) => {
  const [ticketData, setTicketData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolutionNote, setResolutionNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // details, conversation, resolution, assign
  const [availableAdmins, setAvailableAdmins] = useState([]);
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [assigningTicket, setAssigningTicket] = useState(false);
  const [loadingAdmins, setLoadingAdmins] = useState(false);

  const statusColors = {
    open: 'text-red-600 bg-red-50 border-red-200',
    in_progress: 'text-blue-600 bg-blue-50 border-blue-200',
    resolved: 'text-green-600 bg-green-50 border-green-200',
    closed: 'text-gray-600 bg-gray-50 border-gray-200'
  };

  const priorityColors = {
    low: 'text-gray-600 bg-gray-50',
    normal: 'text-blue-600 bg-blue-50',
    high: 'text-orange-600 bg-orange-50',
    urgent: 'text-red-600 bg-red-50'
  };

  // Load ticket details with conversation
  useEffect(() => {
    if (isOpen && ticket?.id) {
      loadTicketDetails();
    }
  }, [isOpen, ticket?.id]);

  // Load admin/moderator list when Assign tab is opened
  useEffect(() => {
    if (activeTab === 'assign' && availableAdmins.length === 0) {
      loadAvailableAdmins();
    }
  }, [activeTab]);

  const loadTicketDetails = async () => {
    setLoading(true);
    try {
      const result = await ChatService.getTicketWithConversation(ticket.id);
      if (result.success) {
        setTicketData(result.data);
        setMessages(result.data.messages || []);
        // Set current assignment if exists
        if (result.data.assigned_to) {
          setSelectedAdminId(result.data.assigned_to);
        }
      }
    } catch (error) {
      console.error('Failed to load ticket details:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const result = await ChatService.getAvailableAdmins();
      if (result.success) {
        setAvailableAdmins(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load available admins:', error);
      alert('Failed to load available admins');
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const result = await ChatService.updateTicket(ticket.id, { status: newStatus });
      if (result.success) {
        setTicketData(prev => ({ ...prev, ...result.data }));
        onUpdate?.(result.data);
      }
    } catch (error) {
      console.error('Failed to update ticket status:', error);
      alert('Failed to update status');
    }
  };

  const handlePriorityChange = async (newPriority) => {
    try {
      const result = await ChatService.updateTicket(ticket.id, { priority: newPriority });
      if (result.success) {
        setTicketData(prev => ({ ...prev, ...result.data }));
        onUpdate?.(result.data);
      }
    } catch (error) {
      console.error('Failed to update ticket priority:', error);
      alert('Failed to update priority');
    }
  };

  const handleSaveResolutionNote = async () => {
    if (!resolutionNote.trim()) return;

    setSavingNote(true);
    try {
      const result = await ChatService.addResolutionNotes(
        ticket.id,
        resolutionNote.trim(),
        ticketData.assigned_admin?.id || 'system'
      );
      if (result.success) {
        setTicketData(prev => ({ ...prev, resolution_notes: result.data.resolution_notes }));
        setResolutionNote('');
        alert('Resolution note added successfully');
      }
    } catch (error) {
      console.error('Failed to add resolution note:', error);
      alert('Failed to add resolution note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleArchive = async () => {
    if (!confirm('Are you sure you want to close and archive this ticket?')) return;

    try {
      const result = await ChatService.archiveTicket(
        ticket.id,
        resolutionNote.trim() || ticketData.resolution_notes
      );
      if (result.success) {
        setTicketData(prev => ({ ...prev, ...result.data }));
        onUpdate?.(result.data);
        alert('Ticket archived successfully');
      }
    } catch (error) {
      console.error('Failed to archive ticket:', error);
      alert('Failed to archive ticket');
    }
  };

  const handleReopen = async () => {
    if (!confirm('Reopen this ticket?')) return;

    try {
      const result = await ChatService.reopenTicket(ticket.id);
      if (result.success) {
        setTicketData(prev => ({ ...prev, ...result.data }));
        onUpdate?.(result.data);
        alert('Ticket reopened successfully');
      }
    } catch (error) {
      console.error('Failed to reopen ticket:', error);
      alert('Failed to reopen ticket');
    }
  };

  const handleAssignTicket = async (adminIdOverride = undefined) => {
    // If adminIdOverride is explicitly passed (including null for unassignment), use it
    // Otherwise fall back to selectedAdminId for normal assignment
    const isUnassigning = adminIdOverride === null;
    const assigneeId = adminIdOverride !== undefined ? adminIdOverride : selectedAdminId;
    
    // Use ticketData.id if available, otherwise fall back to ticket.id prop
    const ticketId = ticketData?.id || ticket?.id;
    
    console.log('[TicketDetail] handleAssignTicket called:', {
      adminIdOverride,
      isUnassigning,
      assigneeId,
      ticketId
    });
    
    if (!ticketId) {
      console.error('[TicketDetail] No ticket ID available');
      alert('Error: Ticket ID not found');
      return;
    }
    
    // Only validate selection if we're assigning (not unassigning)
    if (!isUnassigning && !assigneeId) {
      alert('Please select an admin or moderator to assign');
      return;
    }

    setAssigningTicket(true);
    try {
      // Create a simple object with only needed ticket data to avoid circular references
      const ticketContext = {
        ticket_number: ticketData?.ticket_number,
        title: ticketData?.title,
        priority: ticketData?.priority,
        status: ticketData?.status,
        description: ticketData?.description,
        user: ticketData?.user ? {
          full_name: ticketData.user.full_name
        } : null
      };
      
      const result = await ChatService.assignTicketToAdmin(
        ticketId,
        assigneeId, // Will be null for unassignment, or admin ID for assignment
        ticketContext
      );
      console.log('[TicketDetail] Assignment result success:', result.success);
      
      if (result.success) {
        setTicketData(prev => ({ ...prev, ...result.data }));
        onUpdate?.(result.data);
        if (isUnassigning) {
          alert('Ticket unassigned successfully.');
          setSelectedAdminId(''); // Clear selection after unassignment
        } else {
          alert('Ticket assigned successfully. Email notification sent.');
        }
      } else {
        console.error('[TicketDetail] Assignment failed:', result.error);
        alert(result.error || 'Failed to update ticket assignment');
      }
    } catch (error) {
      console.error('[TicketDetail] Failed to update ticket assignment:', error);
      alert('Failed to update ticket assignment');
    } finally {
      setAssigningTicket(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-text-primary">
                {ticketData?.ticket_number || 'Loading...'}
              </h2>
              <span className={`px-3 py-1 text-sm font-medium rounded-lg border ${statusColors[ticketData?.status || 'open']}`}>
                {ticketData?.status || 'open'}
              </span>
              <span className={`px-3 py-1 text-sm font-medium rounded-lg ${priorityColors[ticketData?.priority || 'normal']}`}>
                {ticketData?.priority || 'normal'} priority
              </span>
            </div>
            <h3 className="text-lg font-medium text-text-primary mb-1">{ticketData?.title}</h3>
            <p className="text-sm text-text-muted">
              Created by {ticketData?.user?.full_name} on {new Date(ticketData?.created_at).toLocaleDateString()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-muted hover:text-text-primary transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 px-6 border-b border-border">
          {[
            { id: 'details', label: 'Details', icon: 'FileText' },
            { id: 'conversation', label: 'Conversation', icon: 'MessageCircle', count: messages.length },
            { id: 'resolution', label: 'Resolution', icon: 'CheckCircle' },
            { id: 'assign', label: 'Assign', icon: 'UserPlus' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 flex items-center gap-2 border-b-2 font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              <Icon name={tab.icon} size={18} />
              {tab.label}
              {tab.count !== undefined && (
                <span className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Details Tab */}
              {activeTab === 'details' && (
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-text-primary mb-2">Description</h4>
                    <p className="text-text-secondary whitespace-pre-wrap">{ticketData?.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-2">Status</label>
                      <select
                        value={ticketData?.status || 'open'}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-2">Priority</label>
                      <select
                        value={ticketData?.priority || 'normal'}
                        onChange={(e) => handlePriorityChange(e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-text-primary mb-2">Customer Information</h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex items-center gap-3">
                        {ticketData?.user?.profile_picture && (
                          <img
                            src={ticketData.user.profile_picture}
                            alt={ticketData.user.full_name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        )}
                        <div>
                          <p className="font-medium text-text-primary">{ticketData?.user?.full_name}</p>
                          <p className="text-sm text-text-muted">{ticketData?.user?.email}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {ticketData?.assigned_admin && (
                    <div>
                      <h4 className="font-semibold text-text-primary mb-2">Assigned To</h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                          {ticketData.assigned_admin.profile_picture && (
                            <img
                              src={ticketData.assigned_admin.profile_picture}
                              alt={ticketData.assigned_admin.full_name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          )}
                          <p className="font-medium text-text-primary">{ticketData.assigned_admin.full_name}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {ticketData?.tags && ticketData.tags.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-text-primary mb-2">Tags</h4>
                      <div className="flex flex-wrap gap-2">
                        {ticketData.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="text-sm text-text-muted">
                    <p>Created: {new Date(ticketData?.created_at).toLocaleString()}</p>
                    {ticketData?.resolved_at && (
                      <p>Resolved: {new Date(ticketData?.resolved_at).toLocaleString()}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Conversation Tab */}
              {activeTab === 'conversation' && (
                <div className="space-y-4">
                  {messages.length === 0 ? (
                    <div className="text-center py-12 text-text-muted">
                      <Icon name="MessageCircle" size={48} className="mx-auto mb-4 opacity-30" />
                      <p>No conversation history</p>
                    </div>
                  ) : (
                    messages.map(message => (
                      <div
                        key={message.id}
                        className={`flex ${message.is_admin ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-xl px-4 py-3 rounded-lg ${
                            message.is_admin
                              ? 'bg-primary text-white'
                              : 'bg-gray-100 text-text-primary'
                          }`}
                        >
                          <p className="text-sm mb-1">{message.message_text}</p>
                          <p className={`text-xs ${message.is_admin ? 'text-primary-100' : 'text-text-muted'}`}>
                            {message.sender?.full_name} • {new Date(message.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Resolution Tab */}
              {activeTab === 'resolution' && (
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-text-primary mb-2">Add Resolution Note</h4>
                    <textarea
                      value={resolutionNote}
                      onChange={(e) => setResolutionNote(e.target.value)}
                      placeholder="Enter resolution details, actions taken, or additional notes..."
                      rows={4}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      disabled={ticketData?.status === 'closed'}
                    />
                    <div className="flex gap-2 mt-2">
                      <Button
                        variant="primary"
                        onClick={handleSaveResolutionNote}
                        disabled={!resolutionNote.trim() || savingNote || ticketData?.status === 'closed'}
                      >
                        {savingNote ? 'Saving...' : 'Add Note'}
                      </Button>
                    </div>
                  </div>

                  {ticketData?.resolution_notes && (
                    <div>
                      <h4 className="font-semibold text-text-primary mb-2">Resolution History</h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <pre className="text-sm text-text-secondary whitespace-pre-wrap font-sans">
                          {ticketData.resolution_notes}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Assign Tab */}
              {activeTab === 'assign' && (
                <div className="space-y-6">
                  {ticketData?.assigned_admin && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Icon name="Info" size={20} className="text-blue-600" />
                        <h4 className="font-semibold text-blue-900">Currently Assigned To</h4>
                      </div>
                      <div className="flex items-center gap-3 ml-8">
                        {ticketData.assigned_admin.profile_picture && (
                          <img
                            src={ticketData.assigned_admin.profile_picture}
                            alt={ticketData.assigned_admin.full_name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        )}
                        <div>
                          <p className="font-medium text-text-primary">{ticketData.assigned_admin.full_name}</p>
                          <p className="text-sm text-text-muted">{ticketData.assigned_admin.email}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <h4 className="font-semibold text-text-primary mb-2">Assign Ticket</h4>
                    <p className="text-sm text-text-muted mb-4">
                      Assign this ticket to an admin or moderator. They will receive an email notification with ticket details.
                    </p>

                    {loadingAdmins ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
                          {availableAdmins.map(admin => (
                            <label
                              key={admin.id}
                              className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                                selectedAdminId === admin.id
                                  ? 'border-primary bg-primary-50'
                                  : 'border-border hover:border-primary-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name="assignee"
                                value={admin.id}
                                checked={selectedAdminId === admin.id}
                                onChange={(e) => setSelectedAdminId(e.target.value)}
                                className="w-4 h-4 text-primary focus:ring-primary"
                              />
                              {admin.profile_picture && (
                                <img
                                  src={admin.profile_picture}
                                  alt={admin.full_name}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              )}
                              <div className="flex-1">
                                <p className="font-medium text-text-primary">{admin.full_name}</p>
                                <p className="text-sm text-text-muted">{admin.email}</p>
                                <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded ${
                                  admin.role === 'admin'
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {admin.role}
                                </span>
                              </div>
                            </label>
                          ))}
                        </div>

                        {availableAdmins.length === 0 && (
                          <div className="text-center py-8 text-text-muted">
                            <Icon name="Users" size={48} className="mx-auto mb-4 opacity-30" />
                            <p>No admins or moderators available</p>
                          </div>
                        )}

                        <div className="flex gap-2">
                          <Button
                            variant="primary"
                            onClick={() => handleAssignTicket()}
                            disabled={!selectedAdminId || assigningTicket}
                          >
                            {assigningTicket ? 'Assigning...' : 'Assign Ticket'}
                          </Button>
                          {ticketData?.assigned_to && (
                            <Button
                              variant="secondary"
                              onClick={() => {
                                if (confirm('Unassign this ticket?')) {
                                  handleAssignTicket(null);
                                }
                              }}
                              disabled={assigningTicket}
                            >
                              Unassign
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-border flex items-center justify-between">
          <div className="flex gap-2">
            {ticketData?.status !== 'closed' ? (
              <Button variant="danger" onClick={handleArchive}>
                <Icon name="Archive" size={18} />
                Close & Archive
              </Button>
            ) : (
              <Button variant="secondary" onClick={handleReopen}>
                <Icon name="RefreshCw" size={18} />
                Reopen Ticket
              </Button>
            )}
          </div>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
